import { Sliders04Icon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { hasMoreInfiniteItems } from '@/Pages/Conversation/utils/hasMoreInfiniteItems';
import Breadcrumbs from '@/components/Breadcrumbs';
import { ErrorBanner } from '@/components/ErrorBanner';
import { Pane } from '@/components/Pane';
import {
  conversationsExtraSchema,
  getConversationExtraValues,
  getFormConversationsExtraValues,
} from '@/components/SessionCrudPane/SessionConversationExtra';
import { Button } from '@/design-system';
import {
  useOrganizationId,
  useRunStats,
  useRunsInfinite,
  useSession,
  useSessionUpdateMutation,
} from '@/hooks/useSwr';
import { RunSchema, SessionSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { getGraphUrlFromRun } from '../Graph/GraphThreadPage.utils';
import { CHILD_RUNS_SELECT } from '../Run/utils/utils';
import { CollatedConversationV2 } from './CollatedConversationV2';
import { ConversationHeaderV2 } from './ConversationHeaderV2';
import { ConversationViewConfigurator } from './ConversationViewConfigurator';
import { THREAD_RUN_SELECT } from './constants';
import {
  EditConversationViewContext,
  EditConversationViewContextProvider,
} from './utils/EditConversationViewContext';

const noop = () => {};

function areArraysEqual(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

function ConversationEditView(props: {
  session: SessionSchema;
  runs: RunSchema[];
}) {
  'use no memo';
  const context = useContext(EditConversationViewContext);

  const defaultValues = useMemo(
    () => getFormConversationsExtraValues(props.session),
    [props.session]
  );

  const defaultSelections = useMemo(
    () => ({
      input:
        typeof defaultValues.input === 'string'
          ? [defaultValues.input]
          : defaultValues.input ?? [],
      output:
        typeof defaultValues.output === 'string'
          ? [defaultValues.output]
          : defaultValues.output ?? [],
    }),
    [defaultValues.input, defaultValues.output]
  );

  const [selectedInputPaths, setSelectedInputPaths] = useState<string[]>(
    defaultSelections.input
  );
  const [selectedOutputPaths, setSelectedOutputPaths] = useState<string[]>(
    defaultSelections.output
  );

  const open = context?.open ?? false;
  const setOpen = context?.setOpen ?? noop;

  useEffect(() => {
    setSelectedInputPaths(defaultSelections.input);
  }, [defaultSelections.input]);

  useEffect(() => {
    setSelectedOutputPaths(defaultSelections.output);
  }, [defaultSelections.output]);

  const mutateSession = useSessionUpdateMutation(props.session.id, {
    onSuccess: () => {
      setOpen(false);
    },
  });

  const handleSave = useCallback(async () => {
    await mutateSession.trigger({
      json: {
        extra: {
          ...props.session.extra,
          conversations: getConversationExtraValues({
            input:
              selectedInputPaths.length > 0 ? selectedInputPaths : undefined,
            output:
              selectedOutputPaths.length > 0 ? selectedOutputPaths : undefined,
          }),
        },
      },
    });
  }, [
    mutateSession,
    props.session.extra,
    selectedInputPaths,
    selectedOutputPaths,
  ]);

  const handleClose = useCallback(() => {
    setSelectedInputPaths(defaultSelections.input);
    setSelectedOutputPaths(defaultSelections.output);
    setOpen(false);
  }, [defaultSelections.input, defaultSelections.output, setOpen]);

  const handleDiscard = () => {
    setSelectedInputPaths(defaultSelections.input);
    setSelectedOutputPaths(defaultSelections.output);
    setOpen(false);
  };

  const hasChanges = useMemo(() => {
    return (
      !areArraysEqual(selectedInputPaths, defaultSelections.input) ||
      !areArraysEqual(selectedOutputPaths, defaultSelections.output)
    );
  }, [
    defaultSelections.input,
    defaultSelections.output,
    selectedInputPaths,
    selectedOutputPaths,
  ]);

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        leftDecorator={Sliders04Icon}
        onClick={() => setOpen(true)}
      >
        Format
      </Button>
      <Pane
        open={open}
        onClose={handleClose}
        title="Format"
        className="p-0"
        topBarRightElement={
          <div className="flex items-center gap-2 pr-6">
            <Button
              variant="outlined"
              color="secondary"
              size="md"
              onClick={handleDiscard}
            >
              Discard Changes
            </Button>
            <Button
              size="md"
              onClick={handleSave}
              disabled={!hasChanges}
              loading={mutateSession.isMutating}
            >
              Save
            </Button>
          </div>
        }
      >
        <div className="flex h-full flex-col gap-4">
          <ConversationViewConfigurator
            runs={props.runs}
            selectedInputPaths={selectedInputPaths}
            selectedOutputPaths={selectedOutputPaths}
            onChangeInputPaths={(value) => setSelectedInputPaths(value)}
            onChangeOutputPaths={(value) => setSelectedOutputPaths(value)}
          />
        </div>
      </Pane>
    </>
  );
}

export function ConversationV2({ isPage = true }: { isPage?: boolean }) {
  const navigate = useNavigate();
  const {
    sessionId,
    conversationId: paramConversationId,
    datasetShareToken,
  } = useParams();
  const [searchParams] = useSearchParams();
  if (!sessionId) throw new Error('Session ID is required');

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'turn' | 'trace'>('turn');
  const handleSetActiveTab = (tab: 'turn' | 'trace') => {
    if (tab === 'trace' && !selectedRunId) {
      setSelectedRunId(rootRuns[0].id);
    }
    if (tab === 'turn' && !rootRuns.find((r) => r.id === selectedRunId)) {
      setSelectedRunId(null);
    }
    setActiveTab(tab);
  };
  useHotkeys('t', () =>
    handleSetActiveTab(activeTab === 'turn' ? 'trace' : 'turn')
  );

  const conversationId =
    paramConversationId ??
    searchParams.get('conversationId') ??
    searchParams.get('peekedConversationId');

  const [searchFilter, setSearchFilter] = useState<string | undefined>(
    undefined
  );
  const filterRuns = useRunsInfinite(
    searchFilter
      ? {
          filter: `and(${searchFilter}, eq(thread_id, "${conversationId}"))`,
          session: [sessionId],
          select: CHILD_RUNS_SELECT,
          skip_prev_cursor: true,
          order: 'asc',
        }
      : null,
    undefined,
    { keepPreviousData: false }
  );

  const rootRunsQuery = useRunsInfinite({
    session: [sessionId],
    is_root: true,
    filter: `eq(thread_id, "${conversationId}")`,
    order: 'asc',
    select: THREAD_RUN_SELECT,
    skip_prev_cursor: true,
  });

  useEffect(() => {
    if (searchFilter) {
      const hasMoreRootRuns = hasMoreInfiniteItems(rootRunsQuery);
      const allFilteredRuns = filterRuns.data?.flat();
      const allRootRuns = rootRunsQuery.data?.flat();
      const hasOrphanedRuns = allFilteredRuns?.some(
        (run) =>
          !allRootRuns?.some((rootRun) =>
            run.parent_run_ids?.includes(rootRun.id)
          )
      );
      if (
        hasOrphanedRuns &&
        hasMoreRootRuns &&
        !rootRunsQuery.isLoading &&
        !rootRunsQuery.isValidating
      ) {
        rootRunsQuery.setSize((size) => size + 1);
      }
    }
  }, [rootRunsQuery, filterRuns, searchFilter]);

  const project = useSession(sessionId);
  const userPlainOptions = conversationsExtraSchema.safeParse(
    project.data?.extra?.conversations
  );

  const plainOptions = userPlainOptions.success
    ? userPlainOptions.data
    : {
        input: { type: 'key-heuristic' as const },
        output: { type: 'key-heuristic' as const },
      };

  const rootRuns = rootRunsQuery.data?.flat() ?? [];
  const organizationId = useOrganizationId();

  const stats = useRunStats(
    {
      filter: `and(eq(thread_id, "${conversationId}"))`,
      is_root: true,
      session: [sessionId],
    },
    { datasetShareToken }
  );

  const graphUrl = getGraphUrlFromRun(organizationId, rootRuns[0]);

  if (rootRunsQuery.isLoading || project.isLoading) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }

  if (rootRunsQuery.error) {
    return <ErrorBanner>{rootRunsQuery.error.message}</ErrorBanner>;
  }

  if (!project.data) {
    return (
      <div>
        <LinearProgress />
      </div>
    );
  }

  const rootRunsTotalLatency = rootRuns.reduce(
    (acc, run) =>
      acc + dayjs(run.end_time).diff(dayjs(run.start_time), 's', true),
    0
  );

  return (
    <EditConversationViewContextProvider>
      <div
        className={cn(
          'flex h-[100vh] w-full flex-col',
          !isPage && 'h-[calc(100vh-60px)]'
        )}
      >
        {isPage && (
          <div className="flex-none border-b border-secondary px-4 pb-3 pt-3">
            <Breadcrumbs />
          </div>
        )}

        <ConversationHeaderV2
          conversationId={conversationId}
          stats={stats.data ?? {}}
          graphUrl={graphUrl}
          onNavigateToGraph={() => graphUrl && navigate(graphUrl)}
          editViewComponent={
            <ConversationEditView session={project.data} runs={rootRuns} />
          }
          activeTab={activeTab}
          setActiveTab={handleSetActiveTab}
          totalLatency={rootRunsTotalLatency}
        />

        {rootRunsQuery.isLoading && (
          <div>
            <LinearProgress />
          </div>
        )}

        <CollatedConversationV2
          rootRunsQuery={rootRunsQuery}
          plainOptions={plainOptions}
          filterRuns={filterRuns}
          searchFilter={searchFilter}
          setSearchFilter={setSearchFilter}
          sessionId={sessionId}
          selectedRunId={selectedRunId}
          setSelectedRunId={setSelectedRunId}
          activeTab={activeTab}
        />
      </div>
    </EditConversationViewContextProvider>
  );
}
